<template>
  <NamiModal
    v-for="({ show, prompt }, id) in shownPromptIds"
    :model-value="show"
    @update:model-value="resolvePrompt(id, 'cancel')"
    darker
  >
    <div class="space-y-4">
      <AsyncImage
        v-if="prompt.icon"
        :src="getAbsoluteAssetLink(`nami/stickers/${prompt.icon}.png`)"
        class="w-48 h-48 mx-auto"
      />
      <h2
        class="font-medium text-lg sm:text-xl text-center dark:text-neutral-50"
      >
        {{ prompt.title }}
      </h2>
      <p v-if="prompt.description" class="dark:text-neutral-200">
        {{ prompt.description }}
      </p>
      <div class="flex gap-2 justify-between">
        <div class="flex gap-2">
          <NamiButton
            v-for="button in getLeftButtons(prompt)"
            :button-type="getButtonType(button)"
            :icon="button.icon"
            no-waves
            @button-click="resolvePrompt(id, button.action)"
          >
            {{ button.text }}
          </NamiButton>
        </div>

        <div class="flex gap-2">
          <NamiButton
            v-for="button in getRightButtons(prompt)"
            :button-type="getButtonType(button)"
            :icon="button.icon"
            no-waves
            @button-click="resolvePrompt(id, button.action)"
          >
            {{ button.text }}
          </NamiButton>
        </div>
      </div>
    </div>
  </NamiModal>
</template>

<script setup lang="ts">
const state = usePromptState();
const { resolve } = usePromptResolver();

const shownPromptIds = reactive<
  Record<
    string,
    {
      show: boolean;
      prompt: Prompt;
    }
  >
>({});

watch(
  () => state.value.prompts,
  (newPrompts) => {
    newPrompts.forEach((prompt) => {
      if (prompt.id in shownPromptIds) return;

      shownPromptIds[prompt.id] = {
        show: true,
        prompt,
      };
    });
  },
  { immediate: true, deep: true },
);

function resolvePrompt(id: string, action: PromptButton["action"]) {
  resolve(id, action);
  shownPromptIds[id].show = false;

  setTimeout(() => {
    const index = state.value.prompts.findIndex((p) => p.id === id);
    if (index === -1) return;
    state.value.prompts.splice(index, 1);
  }, 300);
}

function getLeftButtons(prompt: Prompt) {
  const primaryButtons = prompt.buttons.filter(
    (button) => button.action === "primary",
  );
  const firstDangerButton = prompt.buttons.filter(
    (button) => button.action === "danger",
  );

  return primaryButtons.length > 0
    ? primaryButtons
    : (firstDangerButton ?? primaryButtons);
}

function getRightButtons(prompt: Prompt) {
  const cancelButtons = prompt.buttons.filter(
    (button) => button.action === "cancel",
  );
  const dangerButtons = prompt.buttons.filter(
    (button) => button.action === "danger",
  );
  const hasPrimaryButtons = !!prompt.buttons.find(
    (button) => button.action === "primary",
  );

  return hasPrimaryButtons
    ? [...dangerButtons, ...cancelButtons]
    : [...dangerButtons.slice(1), ...cancelButtons];
}

function getButtonType(button: PromptButton) {
  return button.action === "cancel" ? "secondary" : button.action;
}
</script>
